import { ApiError } from "../../lib/api/core";
import { logAuthFailure, type AuthFailureOperation } from "./authErrorLogging";

export type AuthField = "display_name" | "email" | "password" | "verification_code";

export type AuthUserError = {
  fieldErrors: Partial<Record<AuthField, string>>;
  formError?: string;
};

const FIELD_MESSAGES: Record<AuthField, string> = {
  display_name: "Enter your name",
  email: "Enter a valid email address",
  password: "Password must be at least 8 characters",
  verification_code: "Enter the 6-digit code",
};

const KNOWN_FIELDS = new Set<AuthField>(["display_name", "email", "password", "verification_code"]);

function responseDetail(error: ApiError): unknown {
  if (!error.body || typeof error.body !== "object") return undefined;
  return (error.body as { detail?: unknown }).detail;
}

function responseText(error: unknown): string {
  if (typeof error === "string") return error.toLowerCase();
  if (!(error instanceof ApiError)) return "";
  const detail = responseDetail(error);
  if (typeof detail === "string") return detail.toLowerCase();
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return "";
  const message = (detail as { message?: unknown; msg?: unknown }).message ?? (detail as { msg?: unknown }).msg;
  return typeof message === "string" ? message.toLowerCase() : "";
}

function responseCode(error: unknown): string {
  if (!(error instanceof ApiError)) return "";
  const detail = responseDetail(error);
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return "";
  const code = (detail as { code?: unknown }).code;
  return typeof code === "string" ? code.toLowerCase() : "";
}

function fieldFromLocation(location: unknown): AuthField | undefined {
  if (!Array.isArray(location)) return undefined;
  for (const part of [...location].reverse()) {
    if (typeof part === "string" && KNOWN_FIELDS.has(part as AuthField)) return part as AuthField;
    if (part === "code") return "verification_code";
  }
  return undefined;
}

function validationError(error: unknown): AuthUserError | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const detail = responseDetail(error);
  if (!Array.isArray(detail)) return undefined;

  const fieldErrors: Partial<Record<AuthField, string>> = {};
  for (const item of detail) {
    if (!item || typeof item !== "object") continue;
    const field = fieldFromLocation((item as { loc?: unknown }).loc);
    if (field) fieldErrors[field] = FIELD_MESSAGES[field];
  }
  return Object.keys(fieldErrors).length ? { fieldErrors } : undefined;
}

/** Convert every auth failure shape to fixed copy that is safe to render. */
export function toAuthUserError(error: unknown, operation: AuthFailureOperation): AuthUserError {
  const validation = validationError(error);
  if (validation) return validation;

  const text = responseText(error);
  const code = responseCode(error);
  const signature = `${code} ${text}`;

  if (operation === "register" && ((error instanceof ApiError && error.status === 409) || /email.*already|already.*email/.test(signature))) {
    return { fieldErrors: { email: "This email is already registered" } };
  }
  if (operation === "verify" && /expir/.test(signature)) {
    return { fieldErrors: { verification_code: "The code has expired. Request a new one." } };
  }
  if (operation === "verify" && /invalid.*code|incorrect.*code/.test(signature)) {
    return { fieldErrors: { verification_code: "The code is incorrect. Try again." } };
  }
  if (operation === "verify" && /no.*code|code.*not.*sent/.test(signature)) {
    return { fieldErrors: { verification_code: "Request a new code and try again." } };
  }
  if (operation === "login" && ((error instanceof ApiError && error.status === 401) || /invalid.*credential/.test(signature))) {
    return { fieldErrors: {}, formError: "Incorrect email or password" };
  }
  if (operation === "login" && /email.*not.*verified|unverified/.test(signature)) {
    return { fieldErrors: {}, formError: "Verify your email before signing in" };
  }
  if (operation === "resend" && /wait|cooldown|rate/.test(signature)) {
    return { fieldErrors: {}, formError: "Please wait before requesting another code" };
  }
  if (error instanceof TypeError) {
    return { fieldErrors: {}, formError: "Unable to connect. Check your connection and try again." };
  }
  return { fieldErrors: {}, formError: "Something went wrong. Please try again." };
}

export function resolveAuthError(error: unknown, operation: AuthFailureOperation): AuthUserError {
  logAuthFailure(operation, error);
  return toAuthUserError(error, operation);
}
